/**
 * LensLock Client Map
 * Standalone authenticated app - separate from Photon portal
 * Uses same Permission Service as PhotonUI for access control
 */
import React from "react";
import { useMsal, useIsAuthenticated, useAccount } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { makeStyles, tokens, Button, Spinner, Text } from "@fluentui/react-components";
import { SignOut24Regular, LockClosed24Regular } from "@fluentui/react-icons";
import ClientMap from "./ClientMap";
import { loginRequest } from "./authConfig";
import { usePermissionCheck } from "./usePermissionCheck";

const useStyles = makeStyles({
  container: {
    height: "100vh",
    width: "100vw",
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#f5f5f5",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    backgroundColor: "#004C97",
    color: "white",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalM,
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalM,
  },
  logo: {
    height: "40px",
    width: "auto",
    backgroundColor: "white",
    padding: "4px 8px",
    borderRadius: "4px",
  },
  title: {
    fontSize: "20px",
    fontWeight: 600,
    fontFamily: "'Poppins', sans-serif",
    letterSpacing: "0.5px",
  },
  subtitle: {
    fontSize: "12px",
    opacity: 0.9,
    marginLeft: tokens.spacingHorizontalS,
  },
  userName: {
    fontSize: "14px",
    opacity: 0.9,
  },
  main: {
    flex: 1,
    padding: tokens.spacingVerticalM,
    overflow: "hidden",
  },
  loginContainer: {
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #004C97 0%, #002855 100%)",
    color: "white",
  },
  loginCard: {
    backgroundColor: "white",
    borderRadius: "12px",
    padding: tokens.spacingVerticalXXL,
    textAlign: "center",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
    maxWidth: "400px",
    width: "90%",
  },
  loginLogo: {
    height: "80px",
    marginBottom: tokens.spacingVerticalL,
  },
  loginTitle: {
    fontSize: "24px",
    fontWeight: 600,
    color: "#004C97",
    marginBottom: tokens.spacingVerticalS,
    fontFamily: "'Poppins', sans-serif",
  },
  loginSubtitle: {
    fontSize: "14px",
    color: "#63666A",
    marginBottom: tokens.spacingVerticalL,
  },
  loginButton: {
    width: "100%",
    padding: "12px",
    fontSize: "16px",
  },
  loadingContainer: {
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #004C97 0%, #002855 100%)",
  },
});

/**
 * Login Screen Component
 * Attempts SSO briefly, then shows login button
 */
const LoginScreen: React.FC = () => {
  const styles = useStyles();
  const { instance, inProgress } = useMsal();
  const [error, setError] = React.useState<string | null>(null);
  const [showLoginButton, setShowLoginButton] = React.useState(false);
  const ssoAttemptedRef = React.useRef(false);

  // Guaranteed fallback: always show login button after 3s.
  // Empty deps so MSAL state changes can't cancel this timer.
  React.useEffect(() => {
    const timer = setTimeout(() => setShowLoginButton(true), 3000);
    return () => clearTimeout(timer);
  }, []);

  // Attempt SSO exactly once when MSAL is idle
  React.useEffect(() => {
    if (ssoAttemptedRef.current || inProgress !== InteractionStatus.None) return;
    ssoAttemptedRef.current = true;

    instance.ssoSilent(loginRequest)
      .then((response) => {
        if (response.account) {
          instance.setActiveAccount(response.account);
        }
      })
      .catch(() => {
        setShowLoginButton(true);
      });
  }, [instance, inProgress]);

  const handleLogin = async () => {
    if (inProgress !== InteractionStatus.None) return;

    try {
      setError(null);
      await instance.loginRedirect(loginRequest);
    } catch (err: unknown) {
      console.error("Login error:", err);
      if (err instanceof Error && err.message.includes("interaction_in_progress")) {
        const keysToRemove = Object.keys(sessionStorage).filter(key => 
          key.includes("interaction.status") || 
          key.includes("request.params") ||
          key.includes("request.origin") ||
          key.includes("msal.")
        );
        keysToRemove.forEach(key => sessionStorage.removeItem(key));
        setError("Cleared stale login state. Please click 'Sign in' again.");
      } else {
        setError("Failed to start login. Please try again.");
      }
    }
  };

  if (!showLoginButton) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="large" appearance="inverted" label="Checking sign-in status..." />
      </div>
    );
  }

  return (
    <div className={styles.loginContainer}>
      <div className={styles.loginCard}>
        <img
          src="/lenslock_logo_color_tagline.png"
          alt="LensLock"
          className={styles.loginLogo}
        />
        <div className={styles.loginTitle}>Client Map</div>
        <div className={styles.loginSubtitle}>
          Sign in to view LensLock client locations
        </div>
        {error && (
          <div style={{ color: "red", marginBottom: "16px", fontSize: "14px" }}>
            {error}
          </div>
        )}
        <Button
          className={styles.loginButton}
          appearance="primary"
          onClick={handleLogin}
          disabled={inProgress !== InteractionStatus.None}
        >
          {inProgress !== InteractionStatus.None ? "Signing in..." : "Sign in with Microsoft"}
        </Button>
      </div>
    </div>
  );
};

/**
 * Loading Screen Component
 */
const LoadingScreen: React.FC<{ message?: string }> = ({ message = "Loading..." }) => {
  const styles = useStyles();
  return (
    <div className={styles.loadingContainer}>
      <Spinner size="large" appearance="inverted" label={message} />
    </div>
  );
};

/**
 * Access Denied Screen Component
 */
const AccessDeniedScreen: React.FC<{ error?: string | null }> = ({ error }) => {
  const styles = useStyles();
  const { instance } = useMsal();

  const handleLogout = () => {
    // Local logout only - don't sign out of Microsoft account
    const account = instance.getActiveAccount();
    if (account) {
      instance.setActiveAccount(null);
    }
    sessionStorage.clear();
    window.location.href = "/";
  };

  return (
    <div className={styles.loginContainer}>
      <div className={styles.loginCard}>
        <LockClosed24Regular style={{ fontSize: 48, color: "#d13438", marginBottom: 16 }} />
        <div className={styles.loginTitle} style={{ color: "#d13438" }}>Access Denied</div>
        <div className={styles.loginSubtitle}>
          {error || "You don't have permission to access the Client Map. Please contact your administrator to request access."}
        </div>
        <Button
          className={styles.loginButton}
          appearance="secondary"
          onClick={handleLogout}
        >
          Sign out
        </Button>
      </div>
    </div>
  );
};

/**
 * Main App Component
 */
const App: React.FC = () => {
  const styles = useStyles();
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const account = useAccount();
  const { hasAccess, isLoading: permissionLoading, error: permissionError } = usePermissionCheck();

  const handleLogout = () => {
    // Local logout only - don't sign out of Microsoft account
    const account = instance.getActiveAccount();
    if (account) {
      instance.setActiveAccount(null);
    }
    sessionStorage.clear();
    window.location.href = "/";
  };

  // Show loading only during redirect-based interactions (not ssoSilent)
  if (inProgress === InteractionStatus.HandleRedirect ||
      inProgress === InteractionStatus.Login ||
      inProgress === InteractionStatus.Logout) {
    return <LoadingScreen />;
  }

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return <LoginScreen />;
  }

  // Show loading while checking permissions
  if (permissionLoading) {
    return <LoadingScreen message="Checking permissions..." />;
  }

  // Show access denied if user doesn't have permission
  if (hasAccess === false) {
    return <AccessDeniedScreen error={permissionError} />;
  }

  // Authenticated and authorized - show the app
  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <img
            src="/lenslock_logo_color-RGB-1pt-stroke-wht.png"
            alt="LensLock"
            className={styles.logo}
          />
          <span className={styles.title}>Client Map</span>
          <span className={styles.subtitle}>Find agencies near you</span>
        </div>
        <div className={styles.headerRight}>
          <span className={styles.userName}>
            {account?.name || account?.username || "User"}
          </span>
          <Button
            appearance="subtle"
            icon={<SignOut24Regular />}
            onClick={handleLogout}
            style={{ color: "white" }}
          >
            Sign out
          </Button>
        </div>
      </header>
      <main className={styles.main}>
        <ClientMap />
      </main>
    </div>
  );
};

export default App;
